import React, { useMemo, useCallback } from 'react';
import { AreaClosed, Line, Bar } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { GridRows, GridColumns } from '@visx/grid';
import { scaleTime, scaleLinear } from '@visx/scale';
import { withTooltip, Tooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip';
import { WithTooltipProvidedProps } from '@visx/tooltip/lib/enhancers/withTooltip';
import { localPoint } from '@visx/event';
import { LinearGradient } from '@visx/gradient';
import { max, extent, bisector } from 'd3-array';
import dayjs from 'dayjs';

type Historical = {
  date: string,
  value: number,
}

// background: linear-gradient(180deg, rgba(0, 126, 133, 0.306) 5%, rgba(0, 233, 244, 0.0268421) 87.5%, rgba(0, 243, 255, 0) 100%);
export const background = 'rgba(0, 126, 133, 0.306)';
export const background2 = 'rgba(0, 233, 244, 0.0268421)';
export const accentColor = '#102C2E';
export const accentColorDark = '#75daad';
const tooltipStyles = {
  ...defaultStyles,
  borderRadius: 4,
  color: 'white',
  background: '#102C2E',
  border: '1px solid  rgba(0, 238, 250, 0.45)'
};

// util

// accessors
const getDate = (d: Historical) => new Date(d.date)
const getStockValue = (d: Historical) => d.value;
const bisectDate = bisector<Historical, Date>((d) => new Date(d.date)).left;

export type AreaProps = {
  width: number;
  height: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  historicals: Historical[]
};

export default withTooltip<AreaProps, Historical>(
  ({
    width,
    height,
    margin = { top: 0, right: 0, bottom: 0, left: 0 },
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipTop = 0,
    tooltipLeft = 0,
    historicals,
  }: AreaProps & WithTooltipProvidedProps<Historical>) => {
    if (width < 10) return null;

    // bounds
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // scales
    const dateScale = useMemo(
      () =>
        scaleTime({
          range: [margin.left, innerWidth + margin.left],
          domain: extent(historicals, getDate) as [Date, Date],
        }),
      [innerWidth, margin.left, historicals],
    );
    const historicalsValueScale = useMemo(
      () =>
        scaleLinear({
          range: [innerHeight + margin.top, margin.top],
          domain: [0, (max(historicals, getStockValue) || 0) + innerHeight / 3],
          nice: true,
        }),
      [margin.top, innerHeight, historicals],
    );

    // tooltip handler
    const handleTooltip = useCallback(
      (event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>) => {
        const { x } = localPoint(event) || { x: 0 };
        const x0 = dateScale.invert(x);
        const index = bisectDate(historicals, x0, 1);
        const d0 = historicals[index - 1];
        const d1 = historicals[index];
        let d = d0;
        if (d1 && getDate(d1)) {
          d = x0.valueOf() - getDate(d0).valueOf() > getDate(d1).valueOf() - x0.valueOf() ? d1 : d0;
        }
        showTooltip({
          tooltipData: d,
          tooltipLeft: x,
          tooltipTop: historicalsValueScale(getStockValue(d)),
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [showTooltip, historicalsValueScale, dateScale],
    );

    return (
      <div style={{ width: width -2, height: height - 2, overflow: 'hidden' }}>
        <svg width={width} height={height}>
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="url(#area-background-gradient)"
            rx={14}
          />
          {/* <LinearGradient id="area-background-gradient" from={background} to={background2} /> */}
          <LinearGradient id="area-gradient" from="rgba(0, 126, 133, 0.306)" to="rgba(0, 243, 255, 0) 100%)" toOpacity={0.3} />
          <GridRows
            left={margin.left}
            scale={historicalsValueScale}
            width={innerWidth}
            strokeDasharray="1,3"
            stroke={accentColor}
            strokeOpacity={0}
            pointerEvents="none"
          />
          <GridColumns
            top={margin.top}
            scale={dateScale}
            height={innerHeight}
            strokeDasharray="1,3"
            stroke={accentColor}
            strokeOpacity={0.2}
            pointerEvents="none"
          />
          <AreaClosed<Historical>
            data={historicals}
            x={(d) => dateScale(getDate(d)) ?? 0}
            y={(d) => historicalsValueScale(getStockValue(d)) ?? 0}
            yScale={historicalsValueScale}
            strokeWidth={2}
            stroke="rgba(0, 238, 250, 0.85)"
            fill="url(#area-gradient)"
            curve={curveMonotoneX}
          />
          <Bar
            x={margin.left}
            y={margin.top}
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            rx={14}
            onTouchStart={handleTooltip}
            onTouchMove={handleTooltip}
            onMouseMove={handleTooltip}
            onMouseLeave={() => hideTooltip()}
          />
          {tooltipData && (
            <g>
              <Line
                from={{ x: tooltipLeft, y: margin.top }}
                to={{ x: tooltipLeft, y: innerHeight + margin.top }}
                stroke={accentColorDark}
                strokeWidth={2}
                pointerEvents="none"
                strokeDasharray="5,2"
              />
              <circle
                cx={tooltipLeft}
                cy={tooltipTop + 1}
                r={4}
                fill="black"
                fillOpacity={0.1}
                stroke="black"
                strokeOpacity={0.1}
                strokeWidth={2}
                pointerEvents="none"
              />
              <circle
                cx={tooltipLeft}
                cy={tooltipTop}
                r={4}
                fill={accentColorDark}
                stroke="white"
                strokeWidth={2}
                pointerEvents="none"
              />
            </g>
          )}
        </svg>
        {tooltipData && (
          <div>
            <TooltipWithBounds
              key={Math.random()}
              top={tooltipTop - 12}
              left={tooltipLeft + 12}
              style={tooltipStyles}
            >
              {`$${getStockValue(tooltipData).toLocaleString()}`}
            </TooltipWithBounds>
            <Tooltip
              top={innerHeight + margin.top - 14}
              left={tooltipLeft}
              style={{
                ...defaultStyles,
                width: 'fit-content',
                color: 'rgba(255, 255, 255, 0.55)',
                background: 'transparent',
                textAlign: 'center',
                transform: 'translateX(-50%)',
              }}
            >
              {dayjs(getDate(tooltipData)).format('YYYY MMM DD')}
            </Tooltip>
          </div>
        )}
      </div>
    );
  },
);
